<!-- /* * Confidential and Proprietary. * Do not distribute without 1-800-Flowers.com, Inc. consent. *
Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved. */ -->

{#if !$surveySent && !$sessionTimeout && !$conversationTimeout}
	<div class="chat-input-container" data-testid="chat-input-container" id="chat-input-container">
		<textarea
			data-testid="chat-input"
			bind:value={$messageText}
			on:keydown={(e) => {
				if (e.key === 'Enter') {
					if (!$messageLoading) addMessage($messageText);
					e.preventDefault();
				}
			}}
			on:click={() => {
			  	const isMobile = $chatSession?.deviceType &&
						$chatSession?.deviceType?.toLowerCase() === 'mobile' &&
						$chatSession?.operatingSystem?.toLowerCase() === 'android';
    		  	if (!isMobile) return;
				setTimeout(() => {
				document.getElementById('chat-input')?.scrollIntoView({
					behavior: 'smooth'
				});
				}, 300); 
			}}
			placeholder="Reply"
			class="chat-input"
			id="chat-input"
			tabindex="0"
			disabled={disableInput}
		/>
		<button
			on:click={() => addMessage($messageText)}
			data-testid="chat-input-send-button"
			class="send-button {$messageLoading ? 'disabled' : ''}"
			disabled={$messageLoading}
		>
			<svg
				class="icon {$messageLoading ? 'disabled' : ''}"
				width="22"
				height="22"
				viewBox="0 0 22 22"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					fill-rule="evenodd"
					clip-rule="evenodd"
					d="M21.7071 0.292921C21.9788 0.564559 22.0708 0.967791 21.9439 1.33038L14.9439 21.3304C14.8084 21.7174 14.4504 21.9825 14.0408 21.9992C13.6311 22.0159 13.2527 21.7808 13.0862 21.4062L9.2424 12.7576L0.593867 8.91384C0.219186 8.74731 -0.0158775 8.36894 0.000836285 7.95926C0.0175501 7.54959 0.282654 7.19162 0.669655 7.05617L20.6697 0.0561695C21.0322 -0.0707359 21.4355 0.0212832 21.7071 0.292921ZM11.1928 12.2215L13.8894 18.289L18.6085 4.80571L11.1928 12.2215ZM17.1943 3.3915L3.71108 8.11063L9.77854 10.8073L17.1943 3.3915Z"
					fill="#9D9D9D"
				/>
			</svg>
		</button>
	</div>
{:else}
	<button class="end-conversation-button" on:click={exitChat}>Exit Chat</button>
{/if}

<script lang="ts">
	import { get, writable, type Writable } from 'svelte/store';
	import {
		chatSession,
		chatVisible,
		connectedToAgent,
		messageLoading,
		notificationSoundEnabled,
		surveySent,
		sessionTimeout,
		conversationTimeout
	} from '$lib/stores/chatSessionStore';
	import { sendMessage } from '$lib/apis/webchat/sendMessage';
	import { soundNotification } from '$lib/utilities/soundNotification';
	import { resetIframe } from '../../utilities/draggable';
	import { endConversationLogic } from '../../utilities/endConversation';
	import { sendDynamicsMessage } from '$lib/stores/websocketStore';

	export let typing: Writable<boolean>;
	export let clearSuggestionsInMessages: (
		messages: typeof $chatSession.messages
	) => typeof $chatSession.messages;
	let disableInput: boolean = false;
	$: if ($chatSession?.messages?.length) {
  		const lastMessage = $chatSession.messages[$chatSession.messages.length - 1];
		if (lastMessage?.params?.order_options?.length && lastMessage?.params?.suggestions?.length){
  			disableInput = true;
		} else {
			disableInput = false;
		}
}
	let messageText = writable('');
	const { play } = soundNotification('./ding.mp3');
	const addMessage = async (message: string) => {
		const connectedToDynamics = get(connectedToAgent);
		if (typeof message === 'string' && message.trim() !== '') {
			chatSession.update((old) => {
				const updatedSession = {
					...old,
					messages: [
						...clearSuggestionsInMessages(old.messages),
						{ sender: 'customer', message: message.trim(), type: 'text' }
					]
				};
				sessionStorage.setItem('conversation', JSON.stringify(updatedSession));
				return updatedSession;
			});
			if (connectedToDynamics) {
				sendDynamicsMessage(message, $chatSession.conversation_id, $chatSession.brand);
				messageText.update(() => '');
			} else {
				if ($chatSession?.conversation_id) {
					messageText.update(() => '');
					typing.set(true);
					messageLoading.set(true);
					const returnValue = await sendMessage(
						{
							conversation_id: $chatSession.conversation_id,
							brand_code: $chatSession.brand,
							channel: $chatSession.channel,
							message: message,
							type: 'text',
							params: {
								typing: false,
								passport: {
									passportStatus: !!$chatSession?.passportEndDate
								},
								customer: {
									email: $chatSession.email,
									contact_id: $chatSession.contactId,
									browser_uuid: $chatSession.browseruuid,
									first: $chatSession.firstName,
									last: $chatSession.lastName
								}
							}
						},
						$chatSession.token
					);
					typing.set(false);
					chatSession.update((old) => {
						const updatedSession = {
							...old,
							messages: [
								...clearSuggestionsInMessages(old.messages),
								{
									sender: 'bot',
									message: returnValue?.message,
									type: returnValue?.type,
									params: returnValue?.params || {}
								}
							],
							chatHistory: returnValue?.params?.conversation_history || old.chatHistory
						};
						sessionStorage.setItem('conversation', JSON.stringify(updatedSession));
						return updatedSession;
					});
					if ($notificationSoundEnabled) play();
					messageLoading.set(false);
				}
			}
		}
	};

	const exitChat = async () => {
		resetIframe();
		chatVisible.update((visible) => !visible);
		await endConversationLogic();
	};
</script>

<style>
	.chat-input-container {
		display: flex;
		align-items: center;
		background: #fafafa;
		width: 94%;
		margin-left: 3%;
		border: 2px solid #e9e9e9;
		flex-shrink: 0;
	}
	.chat-input {
		flex: 1;
		padding: 12px;
		border: none;
		outline: none;
		background: transparent;
		font-size: 16px;
		color: #333;
		font-family: area-variable, sans-serif;
		font-style: normal;
		font-variation-settings:
			'INKT' 0,
			'slnt' 0,
			'wdth' 100,
			'wght' 100;
		resize: none;
	}
	.chat-input::placeholder {
		color: #999;
	}
	.send-button {
		transform: rotate(45deg);
		transform-origin: center;
		background: transparent;
		border: none;
		border-radius: 50%;
		width: 36px;
		height: 36px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-left: 8px;
	}
	.send-button:hover {
		color: #199eeb;
	}
	.icon {
		width: 22px;
		height: 22px;
		fill: #9d9d9d;
		transform: rotate(-45deg);
		top: 1px;
		bottom: 1px;
	}
	.icon:hover {
		fill: blue;
	}
	.end-conversation-button {
		font-family: area-variable, sans-serif;
		font-style: normal;
		font-variation-settings:
			'INKT' 0,
			'slnt' 0,
			'wdth' 100,
			'wght' 100;
		width: 90%;
		margin-left: 5%;
		height: 48px;
		border: none;
		background-color: #201648;
		color: white;
		font-size: 16px;
		font-weight: bold;
		border-radius: 5px;
		cursor: pointer;
	}
</style>
